const SubmitToggleFlag = "submitflag";

export class FormSubmitToggle {
    public static DisableForm(formSelector: string): void {
        FormSubmitToggle.DisableFormElements(formSelector);
        FormSubmitToggle.showFormBusyElements();
    }

    public static DisableFormByElement(form: HTMLFormElement): void {
        FormSubmitToggle.disableFormElementsInternal(form);
        FormSubmitToggle.showFormBusyElements();
    }

    private static showFormBusyElements(): void {
        let formBusyElements = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName("form-busy");
        for (const formElement of formBusyElements) {
            formElement.classList.remove("hidden");
        }
    }

    public static EnableForm(formSelector: string): void {
        let formBusyElements = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName("form-busy");
        for (const formElement of formBusyElements) {
            formElement.classList.add("hidden");
        }
        FormSubmitToggle.EnableFormElements(formSelector);
    }

    /**
     * Disables all form elements and buttons within the passed form selector.
     * 
     * Flags elements as disabled so we can re-enable them without interfering with other elements
     * that may have been enabled / disabled by other code.
     * @param formSelector
     */
    public static DisableFormElements(formSelector: string): void {
        let form = document.querySelector(formSelector) as HTMLFormElement;

        FormSubmitToggle.disableFormElementsInternal(form);
    }

    /**
     * Disables all form elements and buttons within the passed form element.
     * 
     * Flags elements as disabled so we can re-enable them without interfering with other elements
     * that may have been enabled / disabled by other code.
     * @param form
     */
    private static disableFormElementsInternal(form: HTMLFormElement): void {
        if (!form) {
            return;
        }

        form.dataset[SubmitToggleFlag] = "true"; // Tag this as disabled by our code so we can find and enable it below.

        let buttons = <NodeListOf<HTMLButtonElement>>form.querySelectorAll("button:not([disabled])");

        for (const button of buttons) {
            button.dataset[SubmitToggleFlag] = "true"; // Tag this as disabled by our code so we can find and enable it below.
            button.disabled = true;
        }

        // Prevent the user from hitting ENTER on a field to submit again.
        let inputs = <NodeListOf<HTMLInputElement>>form.querySelectorAll("input:not([readonly])");

        for (const input of inputs) {

            // Only disable this element if it's not already disabled or readonly.
            if (input.disabled || input.readOnly) {
                continue;
            }

            input.dataset[SubmitToggleFlag] = "true"; // Tag this as disabled by our code so we can find and enable it below.
            if (input.type === "submit") {
                // Disable buttons but not form fields otherwise the form fields don't get submitted.
                input.disabled = true;
            }

            input.readOnly = true;
        }

        document.body.style.cursor = "wait";
    }

    /**
     * Re-enables all form elements and buttons within the passed form selector that were previously disabled
     * by the DisableFormElements function.
     * @param formSelector
     * @returns
     */
    public static EnableFormElements(formSelector: string): void {
        let form = <HTMLFormElement>document.querySelector(formSelector);

        if (!form) {
            return;
        }

        form.removeAttribute(`data-${SubmitToggleFlag}`);

        let buttons = <NodeListOf<HTMLButtonElement>>form.querySelectorAll(`button[data-${SubmitToggleFlag}]`);

        for (const button of buttons) {
            button.removeAttribute(`data-${SubmitToggleFlag}`);
            button.disabled = false;
        }

        let inputs = <NodeListOf<HTMLInputElement>>form.querySelectorAll(`input[data-${SubmitToggleFlag}]`);

        for (const input of inputs) {
            // Only enable this element if we flagged it and then disabled it using our DisableFormElements code above.
            if (input.dataset[SubmitToggleFlag]) {
                input.removeAttribute(`data-${SubmitToggleFlag}`);
                input.disabled = false;
                input.readOnly = false;
            }
        }

        document.body.style.cursor = "default";
    }

    public static IsFormEnabled(formSelector: string): boolean {
        let form = <HTMLFormElement>document.querySelector(formSelector);

        if (!form) {
            return false;
        }

        if (form.dataset[SubmitToggleFlag]) {
            return false;

        }

        return true;
    }

    /**
     * Scans the page for forms with a flag that marks them for automatic disabling on submit
     * if they pass validation. Adds a submit event listener to each form found.
     * 
     * Mark a form for automatic disabling on submit by adding the data-submit-toggle attribute to the form.
     * 
     * Used to prevent repeated hammering of the submit on forms on the client.
     * 
     * Do not use this on forms where you want to do additional js processing on submission.
     * Should only be used on plain forms.
     */
    public static ScanForForms(): void {
        const forms = document.querySelectorAll("form[data-submit-toggle]") as NodeListOf<HTMLFormElement>;

        for (const form of forms) {
            form.addEventListener("submit", (e) => FormSubmitToggle.onFormSubmit(e, form));
        }
    }

    private static onFormSubmit(e: Event, form: HTMLFormElement): void {
        if (!window.validationService.isValid(form)) {
            e.preventDefault();
            return;
        }

        // Form OK, allow submit to go through.
        FormSubmitToggle.DisableFormByElement(form);
    }
}

document.addEventListener("DOMContentLoaded", () => { FormSubmitToggle.ScanForForms(); });
